import { Component, computed, inject, Input, Signal } from '@angular/core';
import { ButtonsModule, IconModule, MenuModule, SpinnerModule } from '@mastechnicaegroup/carelinelive-ui';
import { DatePipe } from '@angular/common';
import { Commissioner } from '@lib/api/models/commissioner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs';
import { Package } from '@lib/api/models/package';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PackageService } from '@lib/package/package.service';
import { PaginationCardComponent } from "@shared/pagination/pagination-card.component";

@Component({
    selector: 'app-commissioner-packages-tab',
    imports: [
    ButtonsModule,
    DatePipe,
    IconModule,
    MenuModule,
    RouterLink,
    PaginationCardComponent,
    SpinnerModule
],
    templateUrl: './commissioner-packages-tab.component.html',
    standalone: true,
    styleUrl: './commissioner-packages-tab.component.scss'
})
export class CommissionerPackagesTabComponent {
    @Input() commissionerData: Commissioner | undefined;
    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _packageService = inject(PackageService);
    private readonly _router = inject(Router);

    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    protected readonly pageResult: Signal<Result<PaginatedApiResponse<Package>> | undefined> = toSignal(
        combineLatest({
            page: toObservable(this.page),
            commissionerId: toObservable(computed(() => this.commissionerData?.id)),
        }).pipe(
            filter(({ commissionerId }) => !!commissionerId),
            distinctUntilChanged((a, b) => a.page === b.page && a.commissionerId === b.commissionerId),
            switchMap(({ page, commissionerId }) =>
                this._packageService.listCommissionerPackages(page, commissionerId!).pipe(toResult())
            ),
            shareReplay(1)
        )
    );

    protected readonly meta = computed(() => {
        return this.pageResult()?.value?.meta;
    });

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }
}
